import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { TotpCodeField } from '@/components/TotpCodeField/TotpCodeField'
import { cancelInstitutionDonorOrder } from '@/lib/institution-donors'
import { ApiClientError } from '@/lib/api/client'
import type { InstitutionDonorOrder } from '@/types/institution-donor'
import styles from './DonorLuckyNumbersSection.module.css'

type Props = {
  donorId: number
  order: InstitutionDonorOrder
  token?: string | null
  onClose: () => void
  onCanceled: (order: InstitutionDonorOrder) => void
}

export function DonorOrderCancelModal({
  donorId,
  order,
  token,
  onClose,
  onCanceled,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const submitting = useRef(false)
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    const element = dialog.current
    element?.showModal()
    return () => {
      active.current = false
      element?.close()
    }
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting.current || reason.trim().length < 10 || !/^\d{6}$/.test(code)) return
    submitting.current = true
    setBusy(true)
    setError('')
    try {
      const result = await cancelInstitutionDonorOrder(
        donorId,
        order.id,
        reason.trim(),
        code,
        token
      )
      if (active.current) onCanceled(result)
    } catch (failure) {
      if (!active.current) return
      setCode('')
      const detail =
        failure instanceof ApiClientError
          ? Object.values(failure.errors ?? {}).flat()[0]
          : undefined
      setError(
        detail ??
          (failure instanceof Error
            ? failure.message
            : 'Não foi possível cancelar o pedido.')
      )
    } finally {
      submitting.current = false
      if (active.current) setBusy(false)
    }
  }

  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="cancel-order-title"
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onClose()
      }}
    >
      <form onSubmit={(event) => void submit(event)}>
        <span className={styles.eyebrow}>Confirmação de segurança</span>
        <h2 id="cancel-order-title" className={styles.title}>
          Cancelar pedido #{order.id}?
        </h2>
        <p className={styles.subtitle}>
          {order.campaign_title ?? `Campanha #${order.campaign_id}`} ·{' '}
          {order.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </p>
        <p className={styles.historyNote}>
          Os números deste pedido serão retirados do cliente e devolvidos ao sistema. O
          pedido continuará no histórico como cancelado.{' '}
          <strong>Esta ação não devolve o dinheiro ao cliente.</strong>
        </p>
        <label className={styles.reasonLabel} htmlFor="cancel-order-reason">
          Motivo do cancelamento
        </label>
        <textarea
          id="cancel-order-reason"
          className={styles.reasonInput}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          minLength={10}
          maxLength={500}
          required
          disabled={busy}
          placeholder="Descreva o motivo com pelo menos 10 caracteres"
          autoFocus
        />
        <TotpCodeField
          id="cancel-order-code"
          value={code}
          onChange={setCode}
          disabled={busy}
        />
        <p className={styles.subtitle}>
          Use um código atual do autenticador. Se já usou este código, aguarde o próximo.
          É necessário ter os dois fatores configurados na sua conta.
        </p>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <div className={styles.dialogActions}>
          <button
            type="button"
            className={styles.moreButton}
            onClick={onClose}
            disabled={busy}
          >
            Manter pedido
          </button>
          <button
            type="submit"
            className={styles.cancelButton}
            disabled={busy || reason.trim().length < 10 || code.length !== 6}
          >
            {busy ? 'Cancelando…' : 'Confirmar cancelamento'}
          </button>
        </div>
      </form>
    </dialog>,
    document.body
  )
}
